import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { safeLinkUrl } from '../../renderer/url-safety';
import { useScene } from './scene-context';

type Props = {
  onComplete: () => void;
};

function initials(primary?: string, secondary?: string): string {
  const a = primary?.trim()[0] ?? '';
  const b = secondary?.trim()[0] ?? '';
  return `${a}${b}`.toUpperCase() || 'IC';
}

export function OpeningSequence({ onComplete }: Props) {
  const { manifest, pack } = useScene();
  const reduceMotion = useReducedMotion();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [opened, setOpened] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const primary = manifest.hero.primaryName ?? manifest.hero.brideName;
  const secondary = manifest.hero.secondaryName ?? manifest.hero.groomName;
  const mark = manifest.typography?.monogram ?? initials(primary, secondary);
  const storageKey = manifest.opening?.skipStorageKey ?? `invitecloud:opened:${manifest.invitationId}`;
  const musicUrl = safeLinkUrl(manifest.music?.url);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => setShowSkip(true), 1800);
    return () => {
      document.body.style.overflow = '';
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) setShowSkip(true);
  }, [reduceMotion]);

  const complete = () => {
    sessionStorage.setItem(storageKey, '1');
    document.body.style.overflow = '';
    onComplete();
  };

  const open = async () => {
    setOpened(true);
    if (audioRef.current) {
      audioRef.current.muted = muted;
      audioRef.current.volume = 0.55;
      try {
        await audioRef.current.play();
      } catch {
        // Autoplay can still fail in some browsers; the visual ritual continues.
      }
    }
    window.setTimeout(complete, reduceMotion ? 450 : 2600);
  };

  const names = useMemo(() => {
    if (primary && secondary) return `${primary} & ${secondary}`;
    return primary ?? secondary ?? manifest.footer.calendar.title;
  }, [manifest.footer.calendar.title, primary, secondary]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden px-5"
      style={{ background: `radial-gradient(circle at 50% 20%, ${pack.colors.secondary} 0%, ${pack.colors.background} 62%)` }}
      initial={{ opacity: 1 }}
      animate={{ opacity: opened ? 0 : 1 }}
      transition={{ duration: reduceMotion ? 0.35 : 0.9, delay: opened && !reduceMotion ? 1.75 : 0.15 }}
    >
      {musicUrl && <audio ref={audioRef} src={musicUrl} loop preload="metadata" />}
      <div className="absolute inset-0 opacity-30" style={{ background: pack.vignette }} />
      <div
        className="absolute inset-x-8 top-8 bottom-8 rounded-[2rem] border"
        style={{ borderColor: `${pack.colors.primary}55`, boxShadow: `inset 0 0 80px ${pack.colors.primary}20` }}
      />

      <motion.button
        type="button"
        onClick={open}
        className="relative h-[min(78vh,620px)] w-[min(88vw,460px)] cursor-pointer border-0 bg-transparent p-0 text-center outline-none"
        aria-label="Open invitation"
        disabled={opened}
        style={{ perspective: 1400 }}
      >
        <motion.div
          className="absolute inset-0 rounded-[1.25rem]"
          style={{
            background: `linear-gradient(145deg, ${pack.colors.text}, ${pack.colors.primary} 44%, ${pack.colors.secondary})`,
            boxShadow: `0 32px 90px ${pack.colors.primary}33`,
            transformOrigin: 'top center',
          }}
          animate={{ rotateX: opened && !reduceMotion ? -118 : 0, y: opened ? -60 : 0 }}
          transition={{ duration: 1.25, ease: [0.2, 0.8, 0.2, 1] }}
        />
        <motion.div
          className="absolute inset-x-5 top-5 bottom-5 flex flex-col items-center justify-center rounded-[1rem] border px-7"
          style={{
            color: pack.colors.background,
            borderColor: `${pack.colors.background}35`,
            background: `linear-gradient(160deg, ${pack.colors.text} 0%, ${pack.colors.primary} 52%, ${pack.colors.text} 100%)`,
          }}
          animate={{ y: opened ? -28 : 0, scale: opened ? 1.03 : 1 }}
          transition={{ duration: 1.1 }}
        >
          <div
            className="mb-8 flex h-24 w-24 items-center justify-center rounded-full border text-3xl font-semibold"
            style={{ borderColor: `${pack.colors.background}55`, fontFamily: pack.fonts.display }}
          >
            {mark}
          </div>
          <p className="text-xs uppercase" style={{ letterSpacing: 0, fontFamily: pack.fonts.body }}>
            {manifest.opening?.subtitle ?? 'A wedding invitation'}
          </p>
          <h1
            className="mt-5 text-5xl leading-none"
            style={{ fontFamily: pack.fonts.script ?? pack.fonts.display }}
          >
            {names}
          </h1>
          <p className="mt-8 text-sm uppercase" style={{ letterSpacing: 0, fontFamily: pack.fonts.body }}>
            Tap to open
          </p>
        </motion.div>
      </motion.button>

      {musicUrl && (
        <button
          type="button"
          onClick={() => {
            const next = !muted;
            setMuted(next);
            if (audioRef.current) audioRef.current.muted = next;
          }}
          className="absolute right-5 top-5 rounded-full border px-4 py-2 text-xs font-semibold"
          style={{ borderColor: `${pack.colors.primary}66`, color: pack.colors.text, background: `${pack.colors.background}aa` }}
        >
          {muted ? 'Unmute' : 'Mute'}
        </button>
      )}

      {showSkip && (
        <button
          type="button"
          onClick={complete}
          className="absolute bottom-6 rounded-full border px-4 py-2 text-xs font-semibold"
          style={{ borderColor: `${pack.colors.primary}66`, color: pack.colors.text, background: `${pack.colors.background}aa` }}
        >
          Skip
        </button>
      )}
    </motion.div>
  );
}
